import React from "react"
import { Row, Col, Card, CardBody, Badge, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap"
import Switch from "react-switch"
import { Vendor } from "./types"

interface VendorsGridProps {
  vendors: Vendor[]
  onToggleStatus: (vendor: Vendor) => void
  onVerify: (vendor: Vendor) => void
  onAdjustCommission: (vendor: Vendor) => void
  onViewDetails: (vendor: Vendor) => void
}

const VendorsGrid: React.FC<VendorsGridProps> = ({
  vendors,
  onToggleStatus,
  onVerify,
  onAdjustCommission,
  onViewDetails
}) => {
  const getVerificationBadgeColor = (verification: string) => {
    switch (verification) {
      case "Verified":
        return "success"
      case "Pending":
        return "warning"
      case "Not Verified":
        return "danger"
      default:
        return "secondary"
    }
  }

  return (
    <Row>
      {vendors.map((vendor) => (
        <Col xl="4" md="6" key={vendor.id} className="mb-3">
          <Card className="shadow-md rounded-3">
            <CardBody>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="d-flex align-items-center">
                  <div className="avatar-md rounded-circle me-3">
                    <img 
                      src={vendor.avatar} 
                      alt={vendor.name}
                      className="rounded-circle"
                      style={{ width: "48px", height: "48px", objectFit: "cover" }}
                    />
                  </div>
                  <div>
                    <h5 className="font-size-16 mb-1 fw-semibold">{vendor.name}</h5>
                    <p className="text-muted mb-0 font-size-12">
                      <i className="bx bx-map me-1"></i>
                      {vendor.location}
                    </p>
                  </div>
                </div>
                <UncontrolledDropdown>
                  <DropdownToggle tag="a" className="text-muted font-size-18" style={{ cursor: "pointer" }}>
                    <i className="bx bx-dots-vertical-rounded"></i>
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem onClick={() => onVerify(vendor)}>
                      <i className="bx bx-check-circle me-2"></i>
                      Verify
                    </DropdownItem>
                    <DropdownItem onClick={() => onAdjustCommission(vendor)}>
                      <i className="bx bx-money me-2"></i>
                      Adjust Commission
                    </DropdownItem>
                    <DropdownItem onClick={() => onViewDetails(vendor)}>
                      <i className="bx bx-show me-2"></i>
                      View Details
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>

              <Row className="mb-3">
                <Col xs="6">
                  <div className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Verification</p>
                    <Badge color={getVerificationBadgeColor(vendor.verification)} className="badge-soft">
                      <i className="bx bx-check-circle me-1"></i>
                      {vendor.verification}
                    </Badge>
                  </div>
                </Col>
                <Col xs="6">
                  <div className="mb-3">
                    <p className="text-muted mb-1 font-size-12">Orders</p>
                    <h5 className="font-size-14 mb-0 fw-medium">{vendor.orders}</h5>
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs="6">
                  <div>
                    <p className="text-muted mb-1 font-size-12">Rating</p>
                    <div className="d-flex align-items-center">
                      <span className="me-2 font-size-14 fw-medium">{vendor.rating}</span>
                      <div className="text-warning">
                        {[...Array(5)].map((_, i) => (
                          <i 
                            key={i}
                            className={`bx ${i < Math.floor(vendor.rating) ? 'bxs-star' : 'bx-star'}`}
                            style={{ fontSize: "16px" }}
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col xs="6">
                  <div>
                    <p className="text-muted mb-1 font-size-12">Revenue Share</p>
                    <h5 className="font-size-14 mb-0 fw-medium">{vendor.revenueShare}</h5>
                  </div>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center pt-3" style={{ borderTop: "1px solid #eff2f7" }}>
                <div className="d-flex align-items-center">
                  <span className="me-2 font-size-12 text-muted">Status:</span>
                  <Switch
                    onColor="#34c38f"
                    offColor="#f46a6a"
                    checked={vendor.status === "Active"}
                    onChange={() => onToggleStatus(vendor)}
                    height={20}
                    width={44}
                    uncheckedIcon={false}
                    checkedIcon={false}
                  />
                </div>
                <span className={`badge ${vendor.status === "Active" ? "badge-soft-success" : "badge-soft-danger"} font-size-12`}>
                  {vendor.status}
                </span>
              </div>
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  )
}

export default VendorsGrid
